import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import rootStore from '~/store';
import layerConfigStore from '~/store2/layerConfig';
import { switchLayerConfigOrganization } from '~/store2/layerConfigSync';
import { useAvailableLayers } from './useAvailableLayers';

/**
 * The layer-config load gate. Whenever the active organization changes (and on first mount) it
 * flushes the previous org's pending edits and bootstraps the new org's GET → not-seeded → seed →
 * hydrate path (see `switchLayerConfigOrganization`). Legacy localStorage writes stay suppressed
 * (see `index.tsx`), so the DB document is the only sink.
 *
 * `bootstrap` is idempotent and ignores responses for a superseded org.
 *
 * Also supplies the available-layer catalog to the store: the picker's section→node-id resolution
 * and ancestor-visibility recompute need the catalog's structure. Same session-long query the
 * Library page uses (`useAvailableLayers` dedupes via react-query).
 *
 * Mount once near the app root (see App2).
 */
export function useLayerConfigBootstrap() {
  const { currentOrganizationId } = useSnapshot(rootStore) as typeof rootStore;
  const catalogQuery = useAvailableLayers(currentOrganizationId);

  useEffect(() => {
    switchLayerConfigOrganization(currentOrganizationId);
  }, [currentOrganizationId]);

  useEffect(() => {
    if (catalogQuery.data) layerConfigStore.setCatalog(catalogQuery.data, currentOrganizationId ?? null);
  }, [catalogQuery.data, currentOrganizationId]);

  // A catalog-load failure (the `GET /v1/availableLayers` query erroring — non-member 403, 5xx,
  // network) must surface as a bootstrap failure: `bootstrap` blocks hydrate on the catalog
  // (`whenCatalogReady`), so without this the picker sits on a silent infinite loader instead of the
  // retry banner. Keyed off the *settled* error (`isError && !isFetching`) so a retry that fails
  // again re-fires it (the `isFetching` true→false edge) rather than regressing to the loader.
  useEffect(() => {
    if (catalogQuery.isError && !catalogQuery.isFetching) {
      layerConfigStore.markCatalogUnavailable(currentOrganizationId ?? null);
    }
  }, [catalogQuery.isError, catalogQuery.isFetching, currentOrganizationId]);
}
